import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { saveContact } from '../services/api';

const AddContactModal = ({ 
  open, 
  onClose, 
  phoneNumber, 
  donors = [], 
  nonprofits = [],
  volunteers = [],
  onSave,
  existingContact = null
}) => {
  const [formData, setFormData] = React.useState({
    type: 'nonprofit',
    name: '',
    parentId: ''
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [profileImage, setProfileImage] = React.useState(null);
  const fileInputRef = React.useRef(null);

  // Reset form when modal opens and pre-populate if existingContact is provided
  React.useEffect(() => {
    if (open) {
      if (existingContact) {
        setFormData({
          type: existingContact.contactType,
          name: existingContact.contactName,
          parentId: existingContact.contactId || ''
        });
      } else {
        setFormData({
          type: 'nonprofit',
          name: '',
          parentId: ''
        });
      }
      setError(null);
      setProfileImage(null);
    }
  }, [open, existingContact]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset parentId when type changes, unless it's 'other'
      ...(name === 'type' ? { parentId: value === 'other' ? 'none' : '' } : {})
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      let profileUrl = null;
      if (profileImage) {
        // Create form data for image upload
        const imageFormData = new FormData();
        imageFormData.append('photo0', profileImage);

        // Upload image
        const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL}/upload/photo`, {
          method: 'POST',
          body: imageFormData,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image');
        }

        const uploadResult = await uploadResponse.json();
        if (uploadResult.files && uploadResult.files.length > 0) {
          profileUrl = uploadResult.files[0].url;
        } else {
          throw new Error('No file URL in upload response');
        }
      }

      // Save contact with profile URL if available
      const response = await saveContact({
        type: formData.type,
        name: formData.name,
        phone: phoneNumber,
        parent_id: formData.type === 'other' ? null : String(formData.parentId),
        profile_url: profileUrl
      });

      onClose();
      // Call onSave callback after successful save
      if (onSave) {
        onSave(response);
      }
    } catch (err) {
      setError(err.message || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  // Get the appropriate list of options based on type
  const getParentOptions = () => {
    switch (formData.type) {
      case 'nonprofit':
        return nonprofits;
      case 'donor':
        return donors;
      case 'volunteer':
        return volunteers;
      default:
        return [];
    }
  };

  // Determine if parent organization is required
  const requiresParent = formData.type !== 'other' && 
    !(formData.type === 'volunteer' && existingContact?.contactType === 'volunteer');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ 
        m: 0, 
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {existingContact ? 'Edit Contact' : 'Add New Contact'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Profile Image Upload */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              ref={fileInputRef}
              onChange={handleImageChange}
            />
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'grey.300',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                overflow: 'hidden',
                '&:hover': {
                  borderColor: 'primary.main',
                }
              }}
              onClick={() => fileInputRef.current.click()}
            >
              {profileImage ? (
                <img
                  src={URL.createObjectURL(profileImage)}
                  alt="Profile preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Typography color="text.secondary">
                  Add Photo
                </Typography>
              )}
            </Box>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Contact Type</InputLabel>
            <Select
              name="type"
              value={formData.type}
              label="Contact Type"
              onChange={handleChange}
            >
              <MenuItem value="nonprofit">Nonprofit</MenuItem>
              <MenuItem value="donor">Donor</MenuItem>
              <MenuItem value="volunteer">Volunteer</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            label="Phone Number"
            value={phoneNumber}
            fullWidth
            disabled
          />

          {requiresParent && (
            <FormControl fullWidth required>
              <InputLabel>
                {formData.type === 'nonprofit' ? 'Nonprofit' : 
                 formData.type === 'donor' ? 'Donor' : 
                 'Volunteer'}
              </InputLabel>
              <Select
                name="parentId"
                value={formData.parentId}
                label={formData.type === 'nonprofit' ? 'Nonprofit' : 
                       formData.type === 'donor' ? 'Donor' : 
                       'Volunteer'}
                onChange={handleChange}
              >
                {getParentOptions().map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {error && (
            <Box sx={{ color: 'error.main', mt: 1 }}>
              {error}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading || !formData.name || (requiresParent && !formData.parentId)}
        >
          {loading ? 'Saving...' : 'Save Contact'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddContactModal; 